import { useState } from "react"
import { useLocation } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { useAppDispatch, useAppSelector } from "hooks/storeHooks"
import { openBillingPaywall, selectBillingEntitlement } from "store/billing/billingSlice"
import { selectUserInfo } from "store/user/userSlice"
import { BillingRequestError, billingApi } from "services/billingApi"
import { useAlert } from "providers/AlertProvider"
import { logger } from "utils/logger"

const ONBOARDING_ENTITLEMENT_STATUSES = new Set(["onboarding_pending", "onboarding_trial"])

export const useBillingPortal = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const location = useLocation()
  const userInfo = useAppSelector(selectUserInfo)
  const billingEntitlement = useAppSelector(selectBillingEntitlement)
  const { showAlert } = useAlert()
  const [isPortalLoading, setIsPortalLoading] = useState(false)

  const canManageBilling =
    !userInfo.groups.some((group) => group.trim().toLowerCase() === "admin") &&
    billingEntitlement !== null &&
    billingEntitlement.lockReason !== "trial_canceled" &&
    billingEntitlement.entitlementStatus !== "manual_contract"

  // Onboarding merchants (free window before/after product setup) have no
  // Stripe customer yet — the portal has nothing to show them (the empty page
  // QA reported on Asana 1217317222911793). They pick a plan in the catalog;
  // the portal is for merchants with an actual Stripe relationship.
  const isOnboardingPlanState = ONBOARDING_ENTITLEMENT_STATUSES.has(
    billingEntitlement?.entitlementStatus ?? ""
  )

  const openPlanCatalog = () => {
    dispatch(openBillingPaywall({ dismissible: true }))
  }

  const openBillingPortal = async () => {
    if (isOnboardingPlanState) {
      openPlanCatalog()
      return
    }

    try {
      setIsPortalLoading(true)
      const session = await billingApi.stripePortalSession({
        returnPath: `${location.pathname}${location.search}`,
      })
      if (!session.url) {
        showAlert({
          error: true,
          text: t(
            "userSettingsPage.manageBillingError",
            "We couldn't open your plan page. Please try again."
          ),
        })
        return
      }
      window.location.assign(session.url)
    } catch (error) {
      // The API refuses a portal for merchants without a Stripe relationship
      // instead of minting an empty customer — route them to the plan catalog.
      if (error instanceof BillingRequestError && error.data?.code === "billing_not_established") {
        openPlanCatalog()
        return
      }
      logger.error("billing", "portal_session_failed", { error })
      showAlert({
        error: true,
        text: t(
          "userSettingsPage.manageBillingError",
          "We couldn't open your plan page. Please try again."
        ),
      })
    } finally {
      setIsPortalLoading(false)
    }
  }

  return { canManageBilling, isPortalLoading, openBillingPortal }
}
